import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle, AlertCircle, Shield, Clock, ChevronDown, AlertTriangle, FileText, MapPin, Wrench } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { CTASection } from "@/components/site/CTASection";
import motoIcon from "@/assets/icons/moto-red.webp";
import calendrierIcon from "@/assets/icons/calendrier-blue.webp";
import { SEOHead } from "@/components/SEOHead";
import { BreadcrumbSchema, FAQSchema } from "@/components/SchemaOrg";
import { DetailedDefects } from "@/components/site/DetailedDefects";
import ducatiStreetfighter from "@/assets/centre/ducati-streetfighter.webp";
import yamahaR1 from "@/assets/centre/yamaha-r1.webp";
import benelli750 from "@/assets/centre/benelli-750-collection.webp";
import scootersAccueil from "@/assets/centre/scooters-accueil.webp";

const motoTypes = [
  "Motos toutes cylindrées",
  "Scooters",
  "Mobylettes",
  "Tricycles (3 roues)",
  "Quads",
  "Voiturettes / VSP",
];

const controlPointsMoto = [
  "Identification du véhicule",
  "Freinage avant et arrière",
  "Direction et guidon",
  "Éclairage et signalisation",
  "Pneus et roues",
  "Suspensions",
  "Niveau sonore",
  "Émissions polluantes",
];

const commonDefectsMoto = [
  { category: "Éclairage", details: "Phare avant mal réglé, feu stop grillé, clignotants HS, catadioptre manquant" },
  { category: "Freinage", details: "Plaquettes usées, disques de frein rainurés, durite poreuse, liquide insuffisant" },
  { category: "Pneus", details: "Usure au-delà du témoin, craquelures dues au vieillissement, pression inadaptée" },
  { category: "Niveau sonore", details: "Échappement non homologué, chicane retirée, db-killer absent" },
  { category: "Suspensions", details: "Fourche avec fuite d'huile, amortisseur arrière fatigué, roulements usés" },
  { category: "Direction", details: "Roulement de direction avec jeu, guidon voilé, jeu dans la colonne" },
];

const detailedDefectsMoto = [
  {
    system: "Éclairage & signalisation",
    percent: 30,
    color: "bg-yellow-500",
    items: [
      { name: "Phare avant mal réglé", description: "éblouissement ou éclairage insuffisant" },
      { name: "Feu stop grillé", description: "non visible par les véhicules derrière" },
      { name: "Clignotants HS", description: "manœuvres dangereuses sans signal" },
      { name: "Catadioptre manquant", description: "visibilité réduite de nuit" },
    ],
  },
  {
    system: "Freinage",
    percent: 25,
    color: "bg-red-500",
    items: [
      { name: "Plaquettes usées", description: "distance de freinage augmentée" },
      { name: "Disques rainurés", description: "freinage irrégulier, vibrations" },
      { name: "Durite poreuse", description: "risque de fuite de liquide" },
      { name: "Liquide insuffisant", description: "perte d'efficacité au freinage" },
    ],
  },
  {
    system: "Pneumatiques",
    percent: 22,
    color: "bg-gray-700",
    items: [
      { name: "Usure au-delà du TWI", description: "adhérence très réduite sous la pluie" },
      { name: "Craquelures", description: "vieillissement du caoutchouc, éclatement possible" },
      { name: "Pression inadaptée", description: "tenue de route compromise" },
      { name: "Flancs endommagés", description: "risque d'éclatement à haute vitesse" },
    ],
  },
  {
    system: "Niveau sonore",
    percent: 18,
    color: "bg-orange-500",
    items: [
      { name: "Échappement non homologué", description: "db au-dessus du seuil réglementaire" },
      { name: "Chicane retirée", description: "niveau sonore excessif" },
      { name: "Db-killer absent", description: "nuisance et non-conformité" },
    ],
  },
  {
    system: "Suspensions",
    percent: 12,
    color: "bg-blue-600",
    items: [
      { name: "Fourche fuyante", description: "fuite d'huile visible sur les tubes" },
      { name: "Amortisseur arrière fatigué", description: "rebond excessif" },
      { name: "Roulements de roue usés", description: "jeu perceptible, bruit" },
    ],
  },
  {
    system: "Direction & transmission",
    percent: 8,
    color: "bg-indigo-500",
    items: [
      { name: "Roulement de direction", description: "jeu, guidon instable" },
      { name: "Chaîne détendue", description: "risque de déraillement" },
      { name: "Pignon/couronne usés", description: "transmission bruyante" },
    ],
  },
];

const MotosPage = () => {
  return (
    <SiteLayout>
      <SEOHead
        title="CT Moto et Scooter dès 70€"
        description="Contrôle technique moto et scooter à Montreuil : obligatoire depuis 2024. Tarif 70€. Expertise 2/3 roues, quads, voiturettes. RDV en ligne."
        path="/vehicules/motos"
      />
      <BreadcrumbSchema items={[{ name: "Accueil", href: "/" }, { name: "Véhicules", href: "/prestations" }, { name: "Motos & 2 Roues", href: "/vehicules/motos" }]} />
      <FAQSchema items={[
        { question: "Le CT moto est-il obligatoire ?", answer: "Oui, depuis avril 2024 pour tous les véhicules de catégorie L : motos, scooters, quads, voiturettes." },
        { question: "Combien coûte le CT moto ?", answer: "À partir de 70€ chez Autosur Montreuil-sous-Bois. Procès-verbal officiel inclus." },
        { question: "Combien de temps dure le CT moto ?", answer: "20 à 30 minutes. Résultat immédiat avec procès-verbal officiel." },
        { question: "Quels documents apporter ?", answer: "Carte grise et permis de conduire. Pour une contre-visite, l'ancien procès-verbal." },
        { question: "Ma moto a-t-elle besoin d'un CT ?", answer: "Dépend de la date d'immatriculation. Avant 2017 : depuis 2024. 2017-2019 : depuis 2025. Après 2020 : à partir de 2026." },
        { question: "Que se passe-t-il si j'échoue ?", answer: "2 mois pour contre-visite sur les points défaillants. À partir de 30€." },
        { question: "Contrôlez-vous les quads et voiturettes ?", answer: "Oui, tous véhicules catégorie L. Même tarif à partir de 70€." },
        { question: "Comment préparer ma moto avant le contrôle ?", answer: "Vérifiez l'éclairage, les pneus, les freins, le niveau sonore et les rétroviseurs. Assurez-vous que l'échappement est homologué." },
        { question: "Le CT est-il le même pour un scooter 50cc ?", answer: "Oui, tous les 2 roues motorisés sont soumis au même contrôle technique depuis 2024, quelle que soit la cylindrée." },
      ]} />
      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-autosur-navy to-autosur-blue overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${yamahaR1})` }}
          />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-body mb-6 border border-white/20">
              <img src={motoIcon} alt="Moto" className="w-6 h-6 brightness-0 invert" />
              Catégorie L
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Contrôle Technique Moto à Montreuil-sous-Bois
            </h1>
            <p className="font-body text-white/80 text-lg mb-4">
              Votre centre Autosur Montreuil-sous-Bois est équipé pour contrôler tous les véhicules 
              de catégorie L : motos, scooters, quads et voiturettes.
            </p>
            <p className="font-body text-white/60 text-sm">
              Inspection 2 roues • CT moto obligatoire • Visite technique deux-roues motorisés
            </p>
          </motion.div>
        </div>
      </section>

      {/* Important notice */}
      <section className="py-8 bg-autosur-red">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex items-center justify-center gap-4 text-white"
          >
            <AlertCircle className="w-6 h-6 flex-shrink-0" />
            <p className="font-heading font-semibold text-center">
              Contrôle technique 2 roues obligatoire depuis 2024 – Prenez rendez-vous maintenant !
            </p>
          </motion.div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-6">
                Le contrôle technique moto : inspection 2 roues obligatoire
              </h2>
              <div className="space-y-4 font-body text-muted-foreground">
                <p>
                  Depuis <strong className="text-autosur-navy">avril 2024</strong>, le <strong className="text-autosur-navy">contrôle technique moto</strong> (CT deux-roues, inspection 2 roues) 
                  est devenu obligatoire pour tous les véhicules de catégorie L : motos, scooters, 
                  cyclomoteurs, tricycles, quads et voiturettes.
                </p>
                <p>
                  Ce contrôle périodique vérifie l'état de sécurité de votre deux-roues motorisé : freinage, 
                  éclairage, direction, niveau sonore et émissions polluantes. Il concerne les véhicules de plus de 5 ans 
                  et doit être renouvelé tous les 2 ou 3 ans selon la catégorie.
                </p>
                <p>
                  Notre centre dispose de l'<strong className="text-autosur-navy">équipement spécifique homologué</strong> pour contrôler toutes les motorisations 
                  et cylindrées, des petits scooters 50cc aux grosses cylindrées sportives et routières.
                </p>
              </div>

              {/* Échéances */}
              <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
                <h3 className="font-heading font-bold text-autosur-navy mb-3 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  Calendrier des échéances du CT moto
                </h3>
                <ul className="space-y-2 font-body text-sm text-autosur-navy">
                  <li>• Véhicules immatriculés avant 2017 : <strong>depuis 2024</strong></li>
                  <li>• Véhicules immatriculés entre 2017 et 2019 : <strong>depuis 2025</strong></li>
                  <li>• Véhicules immatriculés après 2020 : <strong>à partir de 2026</strong></li>
                </ul>
                <p className="font-body text-sm text-muted-foreground mt-3">
                  Consultez notre article complet sur les <Link to="/blog/controle-technique-moto-2026-echeances-obligations" className="text-autosur-blue hover:text-autosur-navy transition-colors underline">échéances CT moto 2026</Link>.
                </p>
              </div>

              {/* Price box */}
              <div className="mt-6 bg-autosur-blue/5 rounded-2xl p-6 border border-autosur-blue/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-heading font-bold text-autosur-navy text-lg">Contrôle technique moto</p>
                    <p className="font-body text-muted-foreground text-sm">Catégorie L</p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-3xl font-bold text-autosur-red">70€</p>
                    <p className="font-body text-muted-foreground text-xs">
                      À partir de
                    </p>
                  </div>
                </div>
                <Link
                  to="/contact"
                  className="mt-4 block w-full py-3 bg-autosur-red text-white font-heading font-semibold text-center rounded-full hover:bg-autosur-red-light transition-colors"
                >
                  Réserver en ligne
                </Link>
                <p className="font-body text-muted-foreground text-[10px] mt-2 text-center">(*) Offre non cumulable</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              {/* Types de véhicules */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-heading font-bold text-autosur-navy mb-4">Véhicules 2 roues acceptés</h3>
                <ul className="grid grid-cols-1 gap-3">
                  {motoTypes.map((type) => (
                    <li key={type} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="font-body text-autosur-navy">{type}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Points de contrôle */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-heading font-bold text-autosur-navy mb-4">Points de contrôle moto</h3>
                <ul className="grid grid-cols-1 gap-2">
                  {controlPointsMoto.map((point) => (
                    <li key={point} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-autosur-blue flex-shrink-0" />
                      <span className="font-body text-autosur-navy text-sm">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Images */}
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img 
                  src={yamahaR1}
                  alt="Contrôle technique moto Yamaha R1 au centre Autosur Montreuil-sous-Bois"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Défauts courants moto */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
              Défauts courants détectés lors du CT moto
            </h2>
            <p className="font-body text-muted-foreground max-w-2xl mx-auto">
              Voici les anomalies les plus fréquentes constatées lors de l'inspection des deux-roues motorisés. 
              Anticipez ces points pour éviter la contre-visite.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {commonDefectsMoto.map((defect, index) => (
              <motion.div
                key={defect.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
              >
                <div className="flex items-center gap-3 mb-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  <h3 className="font-heading font-bold text-autosur-navy">{defect.category}</h3>
                </div>
                <p className="font-body text-muted-foreground text-sm">{defect.details}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Defects Moto */}
      <DetailedDefects
        title="Défauts moto détectés par système"
        subtitle="Voici le détail des anomalies les plus fréquentes par système mécanique lors de l'inspection des deux-roues motorisés, avec leur taux de détection."
        defects={detailedDefectsMoto}
      />

      {/* Préparation moto */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
                Comment préparer votre moto avant le contrôle
              </h2>
              <p className="font-body text-muted-foreground mb-6">
                Une bonne préparation vous évite une contre-visite. Voici les points essentiels à vérifier 
                avant de vous rendre au centre pour votre <strong className="text-autosur-navy">inspection 2 roues</strong>.
              </p>
              <ul className="space-y-3">
                {[
                  "Vérifier tous les feux (phare, feu stop, clignotants, éclairage de plaque)",
                  "Contrôler l'usure des pneus et la pression (TWI visible = refus)",
                  "S'assurer que l'échappement est d'origine ou homologué (db-killer en place)",
                  "Vérifier le jeu dans la direction et les roulements de roue",
                  "Contrôler l'état des plaquettes de frein et le niveau de liquide",
                  "Vérifier l'état de la chaîne ou courroie de transmission",
                  "S'assurer que les rétroviseurs sont présents et en bon état",
                  "Apporter la carte grise originale du véhicule",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="font-body text-autosur-navy text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-4"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={ducatiStreetfighter}
                  alt="Ducati Streetfighter au centre de contrôle technique Autosur"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={scootersAccueil}
                  alt="Scooters à l'accueil du centre Autosur Montreuil-sous-Bois"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <span className="text-autosur-red font-heading font-semibold tracking-widest text-sm mb-4 block">
              EN VIDÉO
            </span>
            <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
              Découvrez le CT moto en action
            </h2>
            <p className="font-body text-muted-foreground max-w-2xl mx-auto">
              Regardez comment se déroule un contrôle technique moto dans notre centre Autosur Montreuil-sous-Bois.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <div className="aspect-video rounded-2xl overflow-hidden shadow-xl">
              <iframe
                src="https://www.youtube.com/embed/qlXcl8DbMHI"
                title="Contrôle technique moto au centre Autosur Montreuil-sous-Bois"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
                className="w-full h-full"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
              Pourquoi choisir Autosur pour votre CT moto ?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { iconSrc: calendrierIcon, title: "RDV en ligne", desc: "Réservez 24h/24" },
              { icon: Shield, title: "Centre agréé", desc: "Homologation État" },
              { icon: Clock, title: "Rapidité", desc: "20-30 minutes" },
              { icon: CheckCircle, title: "Résultat immédiat", desc: "PV instantané" },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 text-center shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-default"
              >
                <div className="w-12 h-12 mx-auto mb-4 bg-autosur-blue/10 rounded-xl flex items-center justify-center">
                  {item.iconSrc ? (
                    <img src={item.iconSrc} alt={item.title} className="w-7 h-7 object-contain" />
                  ) : item.icon ? (
                    <item.icon className="w-6 h-6 text-autosur-blue" />
                  ) : null}
                </div>
                <h3 className="font-heading font-bold text-autosur-navy mb-2">{item.title}</h3>
                <p className="font-body text-muted-foreground text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal links */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="font-heading text-2xl font-bold text-autosur-navy mb-6 text-center">
            Découvrez aussi nos autres contrôles techniques
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto mb-8">
            <Link to="/vehicules/voitures" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all group">
              <h3 className="font-heading font-bold text-autosur-navy text-sm mb-1 group-hover:text-autosur-blue transition-colors">CT voiture</h3>
              <p className="font-body text-muted-foreground text-xs">Berlines, SUV, citadines • Dès 73€</p>
            </Link>
            <Link to="/vehicules/utilitaires" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all group">
              <h3 className="font-heading font-bold text-autosur-navy text-sm mb-1 group-hover:text-autosur-blue transition-colors">CT utilitaire</h3>
              <p className="font-body text-muted-foreground text-xs">Fourgons, camping-cars • Dès 79€</p>
            </Link>
            <Link to="/vehicules/collection" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all group">
              <h3 className="font-heading font-bold text-autosur-navy text-sm mb-1 group-hover:text-autosur-blue transition-colors">CT collection</h3>
              <p className="font-body text-muted-foreground text-xs">Véhicules anciens • Dès 73€</p>
            </Link>
            <Link to="/prestations" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all group">
              <h3 className="font-heading font-bold text-autosur-navy text-sm mb-1 group-hover:text-autosur-blue transition-colors">Tous nos tarifs</h3>
              <p className="font-body text-muted-foreground text-xs">Grille tarifaire complète</p>
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-3xl mx-auto">
            <Link to="/blog/controle-technique-moto-2026-echeances-obligations" className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-100 rounded-xl font-body text-autosur-navy text-sm hover:border-autosur-blue/30 hover:bg-autosur-blue/5 transition-all">
              <FileText className="w-4 h-4 text-autosur-blue flex-shrink-0" />
              CT moto 2026 : échéances
            </Link>
            <Link to="/controle-technique-moto-93" className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-100 rounded-xl font-body text-autosur-navy text-sm hover:border-autosur-blue/30 hover:bg-autosur-blue/5 transition-all">
              <FileText className="w-4 h-4 text-autosur-blue flex-shrink-0" />
              CT moto en Seine-Saint-Denis
            </Link>
            <Link to="/faq" className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-100 rounded-xl font-body text-autosur-navy text-sm hover:border-autosur-blue/30 hover:bg-autosur-blue/5 transition-all">
              <FileText className="w-4 h-4 text-autosur-blue flex-shrink-0" />
              Toutes nos FAQ
            </Link>
          </div>

          <div className="mt-10 text-center">
            <h3 className="font-heading font-bold text-autosur-navy mb-4">CT moto près de chez vous</h3>
            <div className="flex flex-wrap gap-2 justify-center">
              {["controle-technique-bagnolet", "controle-technique-vincennes", "controle-technique-romainville", "controle-technique-les-lilas", "controle-technique-fontenay-sous-bois", "controle-technique-noisy-le-sec"].map((slug) => (
                <Link
                  key={slug}
                  to={`/${slug}`}
                  className="inline-flex items-center gap-1 px-4 py-2 bg-white border border-gray-200 rounded-full font-body text-autosur-navy text-xs hover:border-autosur-blue/50 hover:bg-autosur-blue/5 transition-all"
                >
                  <MapPin className="w-3 h-3 text-autosur-blue" />
                  {slug.replace("controle-technique-", "").replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-autosur-navy mb-8 text-center">
              Questions fréquentes sur le CT moto
            </h2>
            <div className="space-y-4">
              {[
                { q: "Le contrôle technique moto est-il obligatoire ?", a: "Oui, depuis avril 2024, le CT est obligatoire pour tous les véhicules de catégorie L : motos, scooters, quads, voiturettes. Les échéances varient selon la date d'immatriculation. Consultez notre guide des échéances CT moto 2026." },
                { q: "Combien coûte le CT moto ?", a: "Le contrôle technique moto (inspection 2 roues) est à partir de 70€ chez Autosur Montreuil-sous-Bois. Ce tarif inclut l'inspection complète et le procès-verbal officiel." },
                { q: "Combien de temps dure le CT moto ?", a: "Un contrôle technique moto prend généralement 20 à 30 minutes. Le résultat est immédiat avec délivrance du procès-verbal officiel. Parking gratuit pendant le contrôle." },
                { q: "Quels documents apporter pour le CT 2 roues ?", a: "Apportez votre carte grise (certificat d'immatriculation) et votre permis de conduire. Pour une contre-visite, apportez également l'ancien procès-verbal de contrôle technique." },
                { q: "Ma moto a-t-elle besoin d'un CT ?", a: "Si votre moto est immatriculée avant 2017, oui depuis 2024. Entre 2017-2019, depuis 2025. Après 2020, à partir de 2026. Vérifiez votre date d'immatriculation sur votre carte grise." },
                { q: "Que se passe-t-il si j'échoue à la visite technique ?", a: "Vous avez 2 mois pour effectuer les réparations et repasser une contre-visite portant uniquement sur les points défaillants. Contre-visite à partir de 30€." },
                { q: "Contrôlez-vous les quads et voiturettes ?", a: "Oui ! Notre centre contrôle tous les véhicules de catégorie L : motos, scooters, tricycles, quads, voiturettes/VSP. Même tarif à partir de 70€." },
                { q: "Comment préparer ma moto avant l'inspection ?", a: "Vérifiez l'éclairage, les pneus (usure, pression), les freins, le niveau sonore (échappement homologué), les rétroviseurs et la transmission. Ces points simples évitent la majorité des contre-visites." },
                { q: "Le CT est-il le même pour un scooter 50cc et une grosse cylindrée ?", a: "Oui, le contrôle technique est identique pour tous les deux-roues motorisés, quelle que soit la cylindrée. Les points vérifiés sont les mêmes, seuls les critères de mesure s'adaptent au véhicule." },
              ].map((faq, index) => (
                <details key={index} className="bg-white rounded-xl border border-gray-100 shadow-sm group">
                  <summary className="flex items-center justify-between p-5 cursor-pointer font-heading font-semibold text-autosur-navy text-sm hover:text-autosur-blue transition-colors">
                    {faq.q}
                    <ChevronDown className="w-5 h-5 text-muted-foreground group-open:rotate-180 transition-transform flex-shrink-0 ml-4" />
                  </summary>
                  <div className="px-5 pb-5 pt-0">
                    <p className="font-body text-muted-foreground text-sm">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <CTASection 
        title="Votre moto a besoin d'un contrôle ?"
        subtitle="Prenez rendez-vous rapidement avant les périodes chargées."
      />
    </SiteLayout>
  );
};

export default MotosPage;
